import { createReducer, createSelector, on } from '@ngrx/store';
import { AppState } from '../store';
import {
    PAYROLL_REPORT_CREATION_PROCESS_STEP,
    PayrollReportCreationProcessData
} from 'src/app/shared/constants-and-enums/payroll-report-creation-process-types';
import {
    InitPayrollReportCreationForm,
    PayrollReportCreationProcessFailure,
    PayrollReportCreationProcessSuccess,
    PayrollReportCreationProcessUpsertForm,
    ResetPayrollReportCreationForm,
    StartPayrollReportCreationProcess
} from '../actions/payroll-report-creation-process.actions';
import { PayrollReportSummaryDTO } from '@iota/inh-core-v1-management-client';

export interface PayrollReportCreationProcessState {
    step: PAYROLL_REPORT_CREATION_PROCESS_STEP;

    data: PayrollReportCreationProcessData;

    loading: boolean;
    error: boolean;

    latestResults: PayrollReportSummaryDTO[];
}

export const initialState: PayrollReportCreationProcessState = {
    step: undefined,
    data: undefined,
    loading: false,
    error: undefined,
    latestResults: undefined
};

export const payrollReportCreationProcessReducer = createReducer(
    initialState,
    on(ResetPayrollReportCreationForm, () => ({ ...initialState })),
    on(InitPayrollReportCreationForm, () => ({
        ...initialState,
        step: PAYROLL_REPORT_CREATION_PROCESS_STEP.SETTINGS
    })),

    on(PayrollReportCreationProcessUpsertForm, (state, { data }) => ({ ...state, data: { ...(state?.data ?? {}), ...data } })),

    on(StartPayrollReportCreationProcess, (state) => ({ ...state, loading: true, step: PAYROLL_REPORT_CREATION_PROCESS_STEP.RESULT })),
    on(PayrollReportCreationProcessSuccess, (state, { results }) => ({
        ...state,
        loading: false,
        latestResult: results
    })),
    on(PayrollReportCreationProcessFailure, (state, { error }) => ({ ...state, loading: false, latestResult: undefined, error }))
);

export const payrollReportCreationProcessState = (state: AppState) => state.payrollReportCreationProcessState;

export const payrollReportCreationProcessCurrentStep = createSelector(payrollReportCreationProcessState, ({ step }) => step);
export const payrollReportCreationProcessIsInSettingsStep = createSelector(
    payrollReportCreationProcessCurrentStep,
    (step) => step === PAYROLL_REPORT_CREATION_PROCESS_STEP.SETTINGS
);
export const payrollReportCreationProcessIsInResultStep = createSelector(
    payrollReportCreationProcessCurrentStep,
    (step) => step === PAYROLL_REPORT_CREATION_PROCESS_STEP.RESULT
);
export const payrollReportCreationProcessData = createSelector(payrollReportCreationProcessState, ({ data }) => data);
export const payrollReportCreationProcessSelectedCompanies = createSelector(
    payrollReportCreationProcessData,
    (data) => data?.companyIds ?? []
);
export const payrollReportCreationProcessSelectedAsOfDate = createSelector(payrollReportCreationProcessData, (data) => data?.asOfDate);
export const payrollReportCreationProcessDataValid = createSelector(payrollReportCreationProcessData, (data) => {
    const companiesValid = data?.companyIds?.length > 0;
    const asOfDateValid = !!data?.asOfDate;

    return companiesValid && asOfDateValid;
});

export const payrollReportCreationProcessLoading = createSelector(payrollReportCreationProcessState, ({ loading }) => loading);
export const payrollReportCreationProcessError = createSelector(payrollReportCreationProcessState, ({ error }) => error);
export const payrollReportCreationProcessLatestResults = createSelector(
    payrollReportCreationProcessState,
    ({ latestResults }) => latestResults
);
